// @flow
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import type { ConditionalRuleGroup as ConditionalRuleGroupType } from 'types/conditional-rule-group'

import { generateId } from '@/components/CalculationComponent/utils'
import Modal from '@/ui/Modal'
import { addSequentialOrderToGroups } from '@/utils'

import ConditionalRuleGroup from './ConditionalRuleGroups'

function ConditionalRulesModalDialog(): React.Node {
  const {
    questions,
    selectedItem,
    conditionalLogicModalOpen,
    setConditionalLogicModalOpen,
    setQuestion,
  } = React.useContext(FormBuilderContext)

  const [conditionalRuleGroups, setConditionalRuleGroups] = React.useState<
    ConditionalRuleGroupType[],
  >([])
  const [isDirty, setIsDirty] = React.useState(false)

  React.useEffect(() => {
    if (selectedItem?.type === 'question') {
      const question = questions[selectedItem.id]

      const initialGroups =
        question?.conditional_rule_groups?.length > 0
          ? question?.conditional_rule_groups.map((group) => ({
              ...group,
              conditional_rules: group.conditional_rules.map((rule) => ({
                ...rule,
                forQuestion: selectedItem.id,
                isNew: !!rule?.isNew,
              })),
              isNew: !!group?.isNew,
            }))
          : [
              {
                id: 'new-0',
                conditional_rules: [
                  { id: 'new-0', isNew: true, forQuestion: selectedItem.id },
                ],
                isNew: true,
              },
            ]

      const groupsWithOrder = addSequentialOrderToGroups(initialGroups)
      setConditionalRuleGroups(groupsWithOrder)
    }
  }, [selectedItem, questions, conditionalLogicModalOpen])

  React.useEffect(() => {
    setIsDirty(false)
  }, [conditionalLogicModalOpen, conditionalRuleGroups])

  const addRule = React.useCallback(
    (groupId: string) => {
      setConditionalRuleGroups((previous) => {
        const updated = previous.map((group) => {
          if (group.id === groupId) {
            return {
              ...group,
              conditional_rules: [
                ...group.conditional_rules,
                {
                  id: `new-${generateId()}`,
                  isNew: true,
                  forQuestion: selectedItem.id,
                },
              ],
            }
          }
          return group
        })

        return addSequentialOrderToGroups(updated)
      })
    },
    [selectedItem?.id]
  )

  const updateLogicalOperator = React.useCallback(
    (groupId: string, ruleId: string, newLogicalOperator: 'AND' | 'OR') => {
      setConditionalRuleGroups((previousGroups) => {
        const groupIndex = previousGroups.findIndex((g) => g.id === groupId)
        if (groupIndex === -1) return previousGroups

        const newGroups = [...previousGroups]
        const currentGroup = { ...newGroups[groupIndex] }

        const ruleIndex = currentGroup.conditional_rules.findIndex(
          (r) => r.id === ruleId
        )
        if (ruleIndex === -1) return previousGroups

        if (newLogicalOperator === 'OR') {
          const rulesBefore = currentGroup.conditional_rules
            .slice(0, ruleIndex)
            .map((item) => ({
              ...item,
              id: `new-${generateId()}`,
              isNew: true,
            }))
          const rulesAfter = currentGroup.conditional_rules
            .slice(ruleIndex)
            .map((item) => ({
              ...item,
              id: `new-${generateId()}`,
              isNew: true,
            }))

          newGroups[groupIndex] = {
            ...currentGroup,
            conditional_rules: rulesBefore,
          }

          newGroups.splice(groupIndex + 1, 0, {
            ...currentGroup,
            id: `new-${generateId()}`,
            conditional_rules: rulesAfter,
            isNew: true,
          })

          return addSequentialOrderToGroups(newGroups)
        }

        if (newLogicalOperator === 'AND') {
          if (groupIndex === 0) return previousGroups

          const previousGroup = newGroups[groupIndex - 1]

          const mergedRules = [
            ...previousGroup.conditional_rules,
            ...currentGroup.conditional_rules,
          ].map((item) => ({ ...item, id: `new-${generateId()}` }))

          newGroups[groupIndex - 1] = {
            ...previousGroup,
            id: `new-${generateId()}`,
            conditional_rules: mergedRules,
            isNew: true,
          }
          newGroups.splice(groupIndex, 1)

          return addSequentialOrderToGroups(newGroups)
        }

        return previousGroups
      })
    },
    []
  )

  const updateRule = React.useCallback(
    (groupId: string, ruleId: string, field: string, value: string) => {
      setConditionalRuleGroups((previous) => {
        const updated = previous.map((group) => {
          if (group.id === groupId) {
            return {
              ...group,
              conditional_rules: group.conditional_rules.map((rule) => {
                if (rule.id === ruleId) {
                  const shouldClearExpectedAnswer = () =>
                    field === 'question' ||
                    (field === 'operator' &&
                      value !== 'CONTAINS' &&
                      value !== 'NOT_CONTAINS' &&
                      value !== 'CONTAINS_ANY' &&
                      value !== 'NOT_CONTAINS_ANY' &&
                      value !== 'IS_BEFORE' &&
                      value !== 'IS_AFTER' &&
                      (rule.expected_answer ?? '') !== '')
                  const expected_answer = shouldClearExpectedAnswer()
                    ? ''
                    : rule.expected_answer

                  if (field !== 'expected_answer') {
                    const new_rule = {
                      ...rule,
                      expected_answer,
                      [field]: value,
                    }
                    return new_rule
                  } else {
                    let operator = rule.operator
                    if (operator === 'CONTAINS' && value.includes(',')) {
                      operator = 'CONTAINS_ANY'
                    } else if (
                      operator === 'NOT_CONTAINS' &&
                      value.includes(',')
                    ) {
                      operator = 'NOT_CONTAINS_ANY'
                    } else if (
                      operator === 'CONTAINS_ANY' &&
                      !value.includes(',')
                    ) {
                      operator = 'CONTAINS'
                    } else if (
                      operator === 'NOT_CONTAINS_ANY' &&
                      !value.includes(',')
                    ) {
                      operator = 'NOT_CONTAINS'
                    }

                    const new_rule = {
                      ...rule,
                      operator,
                      [field]: value,
                    }
                    return new_rule
                  }
                }
                return rule
              }),
            }
          }
          return group
        })

        return addSequentialOrderToGroups(updated)
      })
    },
    []
  )

  const removeRule = React.useCallback((groupId: string, ruleId: string) => {
    setConditionalRuleGroups((previous) => {
      const updated = previous
        .map((group) => {
          if (group.id === groupId) {
            return {
              ...group,
              conditional_rules: group.conditional_rules.filter(
                (rule) => rule.id !== ruleId
              ),
            }
          }
          return group
        })
        .filter((group) => group.conditional_rules.length > 0)

      return addSequentialOrderToGroups(updated)
    })
  }, [])

  const areRulesValid = React.useCallback(() => {
    let valid = true
    conditionalRuleGroups.forEach((group) => {
      group.conditional_rules.forEach((rule) => {
        if (
          !rule.operator ||
          (!['IS_FILLED', 'IS_EMPTY'].includes(rule.operator) &&
            (!rule.question || !rule.expected_answer)) ||
          (['IS_FILLED', 'IS_EMPTY'].includes(rule.operator) && !rule.question)
        ) {
          valid = false
        }
      })
    })
    return valid
  }, [conditionalRuleGroups])

  const handleSave = React.useCallback(() => {
    setIsDirty(true)

    if (!areRulesValid()) {
      return
    }

    setQuestion(selectedItem.id, {
      ...questions[selectedItem.id],
      hidden: false,
      conditional_rule_groups: conditionalRuleGroups,
    })
    setConditionalLogicModalOpen(false)
  }, [
    areRulesValid,
    conditionalRuleGroups,
    questions,
    selectedItem?.id,
    setQuestion,
    setConditionalLogicModalOpen,
  ])

  return (
    <Modal
      centered
      maskClosable={false}
      okText="Save"
      onCancel={() => setConditionalLogicModalOpen(false)}
      onClose={() => setConditionalLogicModalOpen(false)}
      onOk={handleSave}
      open={conditionalLogicModalOpen}
      title="Visible when"
      width={{
        xs: '90%',
        sm: '80%',
        md: '90%',
        lg: '90%',
        xl: '75%',
        xxl: '60%',
      }}
    >
      <div className="mb-8 mt-8 rounded border !border-gray-200/70 bg-gray-50">
        {conditionalRuleGroups
          .filter((group) => group?.conditional_rules?.length)
          .map((conditionalRuleGroup, index) => (
            <ConditionalRuleGroup
              addRule={addRule}
              conditionalRuleGroup={conditionalRuleGroup}
              groupPosition={index}
              isDirty={isDirty}
              isLastGroup={
                conditionalRuleGroups.filter(
                  (group) => group?.conditional_rules?.length
                ).length ===
                index + 1
              }
              key={conditionalRuleGroup.id}
              removeRule={removeRule}
              updateLogicalOperator={updateLogicalOperator}
              updateRule={updateRule}
            />
          ))}
      </div>
    </Modal>
  )
}

export default ConditionalRulesModalDialog
